import { defineStore } from "pinia";
import {
    SchoolSettings,
    PostingCustomOptions,
    UserPermissions,
} from "@/types/school";
import { useOrganisationStore } from "@/stores/organisationStore";
import { OrganisationProducts } from "@/constants/access";
import { BacsReport, Batch } from "@/types/bacs";
import { ComplianceSummary } from "@/types/compliance";
import { personalAddressAttributes } from "@/types/registration";
import { SelectedCardStatementMonth } from "@/types/cardStatement";

const createDefaultSchoolSettings = (): SchoolSettings =>
    ({
        base_currency_code: "",
        base_currency_symbol: "",
        logo: "",
        name: "",
        shortcode: "",
        uses_wcbs: true, // TODO remove
        uses_bacs: false,
        show_s3_upload_api_keys: true,
        additional_posting_details: {
            timezone_location: "",
            wcbs_api_available: false,
            current_financial_year_start_date: "",
            current_financial_year_end_date: "",
            current_financial_year_code: "",
            current_financial_period: "",
            number_of_financial_periods_per_year: "",
            eligible_for_automatic_posting_method: false,
            calculated_post_until_date: "",
            calculated_current_financial_period: "",
        },
        compliance_required: false,
        terms_conditions_signed: undefined,
        commercial_terms_signed: undefined,
    }) as SchoolSettings;

export const usePiniaStore = defineStore("store", {
    state: () => ({
        userEmail: null as string | null,
        permissions: null as UserPermissions | null,
        schoolShortcodes: [] as string[],
        isHubpay: false,
        dateRange: [
            new Date(new Date().setDate(new Date().getDate() - 7)),
            new Date(),
        ] as [Date, Date],
        singleDate: new Date(),
        schoolSettings: createDefaultSchoolSettings(),
        // Invoices
        loadingTx: true,
        // Payment links
        reloadLinkList: false,
        // Posting settings
        postingCustomOptions: {
            posting_status: "",
            post_until_date: "",
        },
        isAuthenticated: false,
        selectedSupplierAccount: null as string | null,
        selectedPayrollAccount: null as string | null,
        selectedMandateAccount: null as string | null,
        selectedDDAccount: null as string | null,
        refreshUsers: false,
        refreshBacsBatches: false,
        address: null as personalAddressAttributes | null,
        refreshBacsTable: false,
        batchStep: 0,
        batch: null as Batch | null,
        batchFileId: null as string | null,
        noActionRequiredInstructions: false as boolean,
        triggerNextActionOnBatch: false,
        selectedBacsReport: null as BacsReport | null,
        refreshComplianceInformation: false,
        complianceSummary: null as ComplianceSummary | null,
        refreshPortalUsersList: false,
        refreshPostings: false,
        selectedSupplierProfile: null as string | null,
        selectedPayrollProfile: null as string | null,
        selectedMandateProfile: null as string | null,
        selectedDDProfile: null as string | null,
        selectedStatementMonth: null as SelectedCardStatementMonth | null,
    }),
    getters: {
        getUserEmail: (state) => state.userEmail,
        getSchoolShortcodes: () => useOrganisationStore().getSchoolShortcodes,
        getPermissions: () => useOrganisationStore().getPermissions,
        getIsHubpay: (state) => state.isHubpay,
        getSchoolSettings: () =>
            useOrganisationStore().getSchoolSettings ??
            createDefaultSchoolSettings(),
        getAvailableOrganisations: () =>
            useOrganisationStore().getAvailableOrganisations,
        getActiveOrganisationShortcode: () =>
            useOrganisationStore().getActiveOrganisationShortcode,
        getDateRange: (state) => state.dateRange,
        getSingleDate: (state) => state.singleDate,
        getLoadingTx: (state) => state.loadingTx,
        getReloadLinkList: (state) => state.reloadLinkList,
        getPostingCustomOptions: (state) => state.postingCustomOptions,
        getIsAuthenticated: (state) => state.isAuthenticated,
        getSelectedSupplierAccount: (state) => state.selectedSupplierAccount,
        getSelectedPayrollAccount: (state) => state.selectedPayrollAccount,
        getSelectedMandateAccount: (state) => state.selectedMandateAccount,
        getSelectedDDAccount: (state) => state.selectedDDAccount,
        getRefreshUsers: (state) => state.refreshUsers,
        getAddress: (state) => state.address,
        getRefreshBacsBatches: (state) => state.refreshBacsBatches,
        getRefreshBacsTable: (state) => state.refreshBacsTable,
        getBatchStep: (state) => state.batchStep,
        getBatch: (state) => state.batch,
        getBatchFileId: (state) => state.batchFileId,
        getNoActionRequiredInstructions: (state) =>
            state.noActionRequiredInstructions,
        getTriggerNextActionOnBatch: (state) => state.triggerNextActionOnBatch,
        getSelectedBacsReport: (state) => state.selectedBacsReport,
        getRefreshComplianceInformation: (state) =>
            state.refreshComplianceInformation,
        getComplianceSummary: (state) => state.complianceSummary,
        getRefreshPortalUsersList: (state) => state.refreshPortalUsersList,
        getSelectedSupplierProfile: (state) => state.selectedSupplierProfile,
        getSelectedPayrollProfile: (state) => state.selectedPayrollProfile,
        getSelectedMandateProfile: (state) => state.selectedMandateProfile,
        getSelectedDDProfile: (state) => state.selectedDDProfile,
        getSelectedStatementMonth: (state) => state.selectedStatementMonth,
        areContractsSigned: () => {
            const settings = useOrganisationStore().getSchoolSettings;
            if (!settings) return false;

            return (
                settings.terms_conditions_signed !== false &&
                settings.commercial_terms_signed !== false
            );
        },
        /**
         * Checks the school settings to determine which products the organisation has, based on
         * enabled products and signed agreements.
         * @param state
         * @returns
         */
        getOrganisationProducts: (): OrganisationProducts[] => {
            const settings = useOrganisationStore().getSchoolSettings;
            if (!settings) return [];

            const products: OrganisationProducts[] = [];
            if (settings.uses_bacs) products.push(OrganisationProducts.BACS);
            if (settings.uses_wcbs) products.push(OrganisationProducts.WCBS);
            if (settings.adyen_onboarding_status)
                products.push(OrganisationProducts.ADYEN_ONBOARDING);
            if (settings.adyen_onboarding_status)
                products.push(OrganisationProducts.CARD_STATEMENTS);
            if (
                settings.terms_conditions_signed !== undefined ||
                settings.commercial_terms_signed !== undefined
            )
                products.push(OrganisationProducts.CONTRACTS);
            // OrganisationProducts.SOKIN_COMPLIANCE intentionally omitted while hidden
            return products;
        },
    },
    actions: {
        setUserEmail(email: string | null) {
            this.userEmail = email;
        },
        setSchoolShortcodes(shortcodes: string[]) {
            this.schoolShortcodes = shortcodes;
        },
        setPermissions(permissions: UserPermissions | null) {
            this.permissions = permissions;
        },
        clearLegacyOrganisationContext() {
            this.permissions = null;
            this.schoolShortcodes = [];
            this.schoolSettings = createDefaultSchoolSettings();
        },
        clearOrganisationData() {
            this.batch = null;
            this.batchFileId = null;
            this.batchStep = 0;
            this.noActionRequiredInstructions = false;
            this.triggerNextActionOnBatch = false;
            this.selectedBacsReport = null;
            this.selectedSupplierAccount = null;
            this.selectedPayrollAccount = null;
            this.selectedMandateAccount = null;
            this.selectedDDAccount = null;
            this.selectedSupplierProfile = null;
            this.selectedPayrollProfile = null;
            this.selectedMandateProfile = null;
            this.selectedDDProfile = null;
            this.selectedStatementMonth = null;
            this.complianceSummary = null;
            this.postingCustomOptions = {
                posting_status: "",
                post_until_date: "",
            };
        },
        setIsHubpay(status: boolean) {
            this.isHubpay = status;
        },
        setDateRange(range: [Date, Date]) {
            this.dateRange = range;
        },
        setSingleDate(date: Date) {
            this.singleDate = date;
        },
        setLoadingTx(status: boolean) {
            this.loadingTx = status;
        },
        setReloadLinkList(status: boolean) {
            this.reloadLinkList = status;
        },
        setPostingCustomOptions(options: PostingCustomOptions) {
            this.postingCustomOptions = options;
        },
        setIsAuthenticated(authenticatedState: boolean) {
            this.isAuthenticated = authenticatedState;
        },
        setSelectedSupplierAccount(selectedAccount: string | null) {
            this.selectedSupplierAccount = selectedAccount;
        },
        setSelectedPayrollAccount(selectedAccount: string | null) {
            this.selectedPayrollAccount = selectedAccount;
        },
        setSelectedMandateAccount(selectedAccount: string | null) {
            this.selectedMandateAccount = selectedAccount;
        },
        setSelectedDDAccount(selectedAccount: string | null) {
            this.selectedDDAccount = selectedAccount;
        },
        setRefreshUsers(refresh: boolean) {
            this.refreshUsers = refresh;
        },
        setBatchStep(step: number) {
            this.batchStep = step;
        },
        setAddress(address: null | personalAddressAttributes) {
            this.address = address;
        },
        setRefreshBacsBatches(refresh: boolean) {
            this.refreshBacsBatches = refresh;
        },
        setRefreshBacsTable(refresh: boolean) {
            this.refreshBacsTable = refresh;
        },
        setBatch(batch: Batch | null) {
            this.batch = batch;
        },
        setBatchFileId(batchFileId: string | null) {
            this.batchFileId = batchFileId;
        },
        setNoActionRequiredInstructions(noActionRequiredInstructions: boolean) {
            this.noActionRequiredInstructions = noActionRequiredInstructions;
        },
        setTriggerNextActionOnBatch(trigger: boolean) {
            this.triggerNextActionOnBatch = trigger;
        },
        setSelectedBacsReport(bacsReport: BacsReport | null) {
            this.selectedBacsReport = bacsReport;
        },
        setRefreshComplianceInformation(refresh: boolean) {
            this.refreshComplianceInformation = refresh;
        },
        setComplianceSummary(summary: null | ComplianceSummary) {
            this.complianceSummary = summary;
        },
        setRefreshPortalUsersList(refresh: boolean) {
            this.refreshPortalUsersList = refresh;
        },
        setSelectedSupplierProfile(profile: string | null) {
            this.selectedSupplierProfile = profile;
        },
        setSelectedPayrollProfile(profile: string | null) {
            this.selectedPayrollProfile = profile;
        },
        setSelectedMandateProfile(profile: string | null) {
            this.selectedMandateProfile = profile;
        },
        setSelectedDDProfile(profile: string | null) {
            this.selectedDDProfile = profile;
        },
        setSelectedStatementMonth(
            monthObject: SelectedCardStatementMonth | null,
        ) {
            this.selectedStatementMonth = monthObject;
        },
    },
    persist: [
        {
            pick: [
                "userEmail",
                "schoolShortcodes",
                "permissions",
                "isHubpay",
                "schoolSettings",
            ],
            storage: localStorage,
        },
        {
            pick: [
                "userEmail",
                "dateRange",
                "singleDate",
                "loadingTx",
                "reloadLinkList",
                "postingCustomOptions",
                "isAuthenticated",
                "selectedSupplierAccount",
                "selectedPayrollAccount",
                "selectedMandateAccount",
                "selectedDDAccount",
                "refreshUsers",
                "address",
                "refreshBacsBatches",
                "refreshBacsTable",
                "batchStep",
                "batch",
                "batchFileId",
                "noActionRequiredInstructions",
                "triggerNextActionOnBatch",
                "selectedBacsReport",
                "refreshComplianceInformation",
                "complianceSummary",
                "refreshPortalUsersList",
                "selectedSupplierProfile",
                "selectedPayrollProfile",
                "selectedMandateProfile",
                "selectedDDProfile",
                "selectedStatementMonth",
            ],
            storage: sessionStorage,
        },
    ],
});
/**
 * Keeps components that read legacy shortcodes and permissions aligned with the active organisation
 * by syncing the active organisation's shortcodes and permissions to the legacy Pinia store.
 * */
export const syncActiveOrganisationToLegacyStore = () => {
    const organisationStore = useOrganisationStore();
    const permissions = organisationStore.getPermissions;
    if (!permissions) return;

    const piniaStore = usePiniaStore();
    piniaStore.setSchoolShortcodes(organisationStore.getSchoolShortcodes);
    piniaStore.setPermissions(permissions);
};
